import type { ReactElement } from 'react';

import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';

import type { CompoundInputValue } from '../CompoundInput/types';
import type {
  FormFieldCheckboxGroupProps,
  FormFieldCompoundInputProps,
  FormFieldFileInputProps,
  FormFieldInputPropsUnion,
  FormFieldProps,
  FormFieldRadioGroupProps,
  FormFieldSelectProps,
  FormFieldTextAreaProps,
} from './types';

import { typedMemo } from '../../utils/typedMemo';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import { FormFieldControl, isGroupFormField } from './controls';
import './FormField.css';

const messages = defineMessages({
  requiredIndicator: {
    id: 'FormField.requiredIndicator',
    defaultMessage: '*',
    description:
      'Visual required-field asterisk. Hidden from assistive technology via aria-hidden.',
  },
});

type ControlPrivateProps = {
  ariaDescribedBy?: string;
  id: string;
};

function FieldLabel({
  label,
  accessibleLabel,
}: {
  label: string;
  accessibleLabel?: string;
}) {
  if (!accessibleLabel) {
    return (
      <Text as="span" variant="body-semi-m">
        {label}
      </Text>
    );
  }

  return (
    <Text as="span" variant="body-semi-m">
      <span aria-hidden="true">{label}</span>
      <span className="eds-styles--visually-hidden">{accessibleLabel}</span>
    </Text>
  );
}

function FormFieldComponent<V = string>(
  props: FormFieldSelectProps<V>,
): ReactElement;
function FormFieldComponent<V = string>(
  props: FormFieldRadioGroupProps<V>,
): ReactElement;
function FormFieldComponent<V = string>(
  props: FormFieldCheckboxGroupProps<V>,
): ReactElement;
function FormFieldComponent<C extends CompoundInputValue>(
  props: FormFieldCompoundInputProps<C>,
): ReactElement;
function FormFieldComponent(props: FormFieldTextAreaProps): ReactElement;
function FormFieldComponent(props: FormFieldFileInputProps): ReactElement;
function FormFieldComponent(props: FormFieldInputPropsUnion): ReactElement;
function FormFieldComponent<
  V = string,
  C extends CompoundInputValue = CompoundInputValue,
>(props: FormFieldProps<V, C>) {
  const {
    hint,
    label,
    layout = 'stacked',
    name,
    required,
    status,
    __private,
  } = props;
  const intl = useIntl();
  const labelId = `${name}-label`;
  const helperId = `${name}-helper`;

  const hasStatus = Boolean(status);
  const showHelper = Boolean(hint);
  const isRow = layout === 'row';
  const isGroup = isGroupFormField(props);

  const privateProps: ControlPrivateProps = {
    ariaDescribedBy: showHelper ? helperId : undefined,
    id: name,
  };

  const control = (
    <FormFieldControl
      labelId={labelId}
      privateProps={privateProps}
      props={props}
    />
  );

  const labelElement = (
    <Layout
      align="center"
      as={isGroup ? 'div' : 'label'}
      className={clsx(isRow && 'eds-form-field--label')}
      htmlFor={isGroup ? undefined : name}
      id={labelId}
      spacing="xs"
    >
      <FieldLabel accessibleLabel={__private?.accessibleLabel} label={label} />
      {required ? (
        <span aria-hidden="true">
          <Text as="span" tone="critical" variant="body-semi-m">
            {intl.formatMessage(messages.requiredIndicator)}
          </Text>
        </span>
      ) : null}
    </Layout>
  );

  return (
    <Layout
      aria-labelledby={labelId}
      className={clsx('eds-form-field', isRow && 'eds-form-field--row')}
      direction="column"
      spacing="xs"
    >
      {isRow ? (
        <Layout align="center" spacing="m">
          {labelElement}
          <Layout className="eds-form-field--control" flex="1">
            {control}
          </Layout>
        </Layout>
      ) : (
        <>
          {labelElement}
          {control}
        </>
      )}
      {showHelper && !hasStatus ? (
        <Layout className="eds-form-field--helper" id={helperId}>
          <Text as="p" tone="subdued" variant="body-s">
            {hint}
          </Text>
        </Layout>
      ) : null}
      {showHelper && hasStatus ? (
        <Layout
          align="center"
          className={clsx(
            'eds-form-field--helper',
            `eds-form-field--message-${status}`,
          )}
          id={helperId}
          spacing="xs"
        >
          <Icon icon="warning-exclamation" size="s" />
          <Text
            as="p"
            tone={status === 'critical' ? 'critical' : 'auto'}
            variant="body-semi-s"
          >
            {hint}
          </Text>
        </Layout>
      ) : null}
    </Layout>
  );
}

export const FormField = typedMemo(FormFieldComponent);

FormField.displayName = 'FormField';
